// src/containers/reportBuilder/ReportPreviewSection.jsx
//
// Picks and renders the appropriate report preview for the current
// selection — MultiProgramPreview for a pure multi-program selection,
// ReportPreview for everything else (single, program, multi-single, mixed).
// Purely presentational: all per-activity detail data (venues, objectives,
// evaluation plans, chart images, etc.) is fetched by the parent
// (ReportBuilderPanel) since some of it — evaluationPlans, chartImages — is
// also needed by the sibling Charts tab, not just this preview.
//
// Shared by the admin Report Builder page and any embedded single-activity
// usage (e.g. the Activity page's "Custom Report" slideout) — the only
// thing that differs between those two contexts is how `selectedActivities`
// got chosen, not what happens once it's known. `onEdit`/`onClear` are
// optional — omit them (embedded usage) to hide the "← Selection"
// back-button, since there's nothing to go back to when the activity is fixed.

import MultiProgramPreview from './MultiProgramPreview';
import ReportPreview from './ReportPreview';

// ── Template type detection ───────────────────────────────────────────────────

export const detectTemplateType = (activities) => {
  if (!activities.length) return null;
  const programs = activities.filter((a) => a.isProgram);
  const singles = activities.filter((a) => !a.isProgram);
  if (activities.length === 1) return singles.length ? 'single' : 'program';
  if (programs.length === activities.length) return 'multi-program';
  if (singles.length === activities.length) return 'multi-single';
  return 'mixed';
};

export default function ReportPreviewSection({
  selectedActivities,
  orgId,
  orgName,
  prefs,
  includedCharts,
  chartSettings,
  assistPrefs,
  reportAssistEnabled,
  venues,
  objectives,
  crossRefs,
  fastFacts,
  evaluationPlans,
  chartImages,
  shortlistedComments,
  surveyResponseComments,
  onEdit,
  onClear,
}) {
  const templateType = detectTemplateType(selectedActivities);

  if (templateType === 'multi-program') {
    return (
      <MultiProgramPreview
        selectedActivities={selectedActivities}
        orgName={orgName}
        orgId={orgId}
        prefs={prefs}
        includedCharts={includedCharts}
        chartSettings={chartSettings}
        reportAssistEnabled={reportAssistEnabled}
        assistPrefs={assistPrefs}
        onEdit={onEdit}
        onClear={onClear}
      />
    );
  }

  return (
    <ReportPreview
      selectedActivities={selectedActivities}
      venues={venues}
      objectives={objectives}
      crossRefs={crossRefs}
      fastFacts={fastFacts}
      evaluationPlans={evaluationPlans}
      shortlistedComments={shortlistedComments}
      surveyResponseComments={surveyResponseComments}
      orgName={orgName}
      orgId={orgId}
      prefs={prefs}
      includedCharts={includedCharts}
      chartImages={chartImages}
      assistPrefs={assistPrefs}
      onEdit={onEdit}
      onClear={onClear}
    />
  );
}
